import { useContext, createContext, useMemo } from 'react';
import { useStore } from 'zustand';
/**
 * @typedef {import("../../../libraries/threads").default} TopicsInstance
 */


const TopicsContext = createContext(null);

/**
 * 
 * @returns {TopicsInstance}
 */
export const useTopicsProvider = () => {
	const context = useContext(TopicsContext);
	if (!context) {
		throw new Error('useTopicsProvider must be used within a TopicsProvider');
	}
	return context;
};

/**
 * 
 * @returns {TopicsInstance}
 */
export const useScopedTopicsInstance = () => {
	const topicsInstance = useTopicsProvider();
	const storeKey = useCurrentStoreKey();

	// Memoize the wrapped instance
	return useMemo(() => {
		// Only wrap methods that actually need scoping
		const scopedMethods = {
			setThreadsAndComments: (data) =>
				topicsInstance.setThreadsAndComments(data, storeKey),
			setRepliesToComment: (commentId, replies) =>
				topicsInstance.setRepliesToComment(commentId, replies, storeKey),
			hideRepliesFromComment: (commentId) =>
				topicsInstance.hideRepliesFromComment(commentId, storeKey),
			postReplyToComment: (commentId, replyData) =>
				topicsInstance.postReplyToComment(commentId, replyData, storeKey),

			deleteThreadFromStore: (threadId) =>
				topicsInstance.deleteThreadFromStore(threadId, storeKey),
			addThreadComment: (threadId, commentData) =>
				topicsInstance.addThreadComment(threadId, commentData, storeKey),

		};

		// Create object with all original methods plus scoped overrides
		return Object.assign(
			Object.create(Object.getPrototypeOf(topicsInstance)),
			topicsInstance,
			scopedMethods
		);
	}, [topicsInstance, storeKey]);
};

/**
 * 
 * @param {{topicsInstance: TopicsInstance}} props 
 * @returns 
 */
export const TopicsProvider = ({ children, topicsInstance }) => {
	// always give access to the DataStore as well
	return <TopicsContext.Provider value={topicsInstance}>
		{children}
	</TopicsContext.Provider>
}



const TopicsStoreContext = createContext(null);
const TopicsStoreKeyContext = createContext(null);

/**
 * @param {{ children: React.ReactNode, storeKey: String}} props 
 */
export const TopicsStoreProvider = ({ children, storeKey }) => {

	const topicsInstance = useTopicsProvider();
	const store = topicsInstance.getStore(storeKey);

	if (!store) {
		throw new Error(`No store found for key: ${storeKey}`);
	}

	return (
		<TopicsStoreKeyContext.Provider value={storeKey}> {/* Provide the key */}
			<TopicsStoreContext.Provider value={store}>
				{children}
			</TopicsStoreContext.Provider>
		</TopicsStoreKeyContext.Provider>
	);
};

export const useCurrentStoreKey = () => {
	const storeKey = useContext(TopicsStoreKeyContext);
	if (!storeKey) throw new Error("useCurrentStoreKey must be used inside TopicsStoreProvider");
	return storeKey;
};

/**
 * Hook to access a specific Topics store.
 * @template T
 * @param {(state:  import('../../../libraries/threads/types').TopicsStore) => T} selector - Zustand selector function
 * @returns {T} The selected part of the Topics store
 */
export const useTopicsStoreProvider = (selector) => {
	const store = useContext(TopicsStoreContext);
	if (!store) throw new Error("useTopicsStoreProvider must be used inside TopicsStoreProvider");

	return useStore(store, selector);
};


/**
 * Hook to access a specific Topics store.
 * @template T
 * @param {(state:  import('../../../libraries/threads/types').TopicsDataStore) => T} selector - Zustand selector function
 * @returns {T} The selected part of the Topics store
 */
export const useTopicsDataStore = (selector) => {
	const topicsInstance = useTopicsProvider();
	const dataStore = topicsInstance.getDataStore();
	if (!dataStore) throw new Error("No data store found in topicsInstance");
	return useStore(dataStore, selector);
}


const TopicsPanelContext = createContext(null);

export const useTopicsPanelProvier = () => {
	const context = useContext(TopicsPanelContext);
	return context;
};

export const TopicsPanelProvider = ({ children, value }) => (
	<TopicsPanelContext.Provider value={value}>
		{children}
	</TopicsPanelContext.Provider>
);









// low level contexts
const ThreadContext = createContext(null);

/**
 * 
 * @returns {import('../../../libraries/threads/types').Thread}
 */
export const useThreadContext = () => {
	const context = useContext(ThreadContext);
	if (!context) {
		throw new Error('useThreadContext must be used within a ThreadContextProvider');
	}
	return context;
};

export const ThreadContextProvider = ({ children, thread }) => {
	return <ThreadContext.Provider value={thread}>
		{children}
	</ThreadContext.Provider>
}

export const CommentContext = createContext(null);

/**
 * 
 * @returns {import('../../../libraries/threads/types').Comment} comment
 */
export const useCommentContext = () => {
	const context = useContext(CommentContext);
	if (!context) {
		throw new Error('useCommentContext must be used within a CommentContextProvider');
	}
	return context;
};

export const CommentContextProvider = ({ children, comment }) => {
	return <CommentContext.Provider value={comment}>
		{children}
	</CommentContext.Provider>
};


// // Helper to access store *outside* React components
// export const getThreadStore = () => {
// 	const store = useContext(TopicsStoreContext);
// 	if (!store) throw new Error("getThreadStore must be used inside ThreadStoreProvider");
// 	return store;
// };

// export const CreateFloatingThreadStoreContext = createContext(null);

// export const CreateFloatingThreadStoreProvider = ({ children, _key }) => {
// 	const _main = useTopicsProvider().this;
// 	const store = createCreateFloatingThreadStore(_main);
// 	_main.setStore(_key, store);

// 	return (
// 		<CreateFloatingThreadStoreContext.Provider value={store}>
// 			{children}
// 		</CreateFloatingThreadStoreContext.Provider>
// 	);
// };


// export const useCreateFloatingThreadStoreContext = (selector) => {
// 	const store = useContext(CreateFloatingThreadStoreContext);
// 	if (!store) throw new Error("useCreateFloatingThreadStore must be used inside CreateFloatingThreadStoreProvider");
// 	return useStore(store, selector);
// };
